import { useEffect, useState } from 'react';
import { useMultiStepForm } from 'app/hooks/useMultiStepForm';
import { MasterTradeParams } from 'app/models/master-trade-params';
import EditReferencesForm from './edit-references-form';
import EditTermsOfTradeForm from './edit-term-of-trades-form';
import EditCounterpartiesForm from './edit-counterparty-form';
import EditBookingForm from './edit-booking-form';
import StepperModal from 'app/views/components/modals/modal-stepper/StepperModal';
import IonIcon from '@reacticons/ionicons';

export enum Tab {
  Booking,
  References,
  Counterparty,
  TermsOfTrade,
}

type MultiPartFormProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  tradeToEdit?: MasterTradeParams;
};

export default function EditTradeAssetsModal({ open, setOpen, tradeToEdit }: MultiPartFormProps) {
  const arrowIcon = <IonIcon name="arrow-forward-outline" style={{ color: '#1A56DB' }} />;

  const [tab, setTab] = useState<Tab>(Tab.Booking);
  const [masterTrade, setMasterTrade] = useState<MasterTradeParams | undefined>(undefined);
  const [submitAction, setSubmitAction] = useState<string | undefined>(undefined);

  function handleSubmit() {
    if (submitAction === 'saveAndSubmit') {
      handleSaveAndSubmit();
    } else {
      handleSaveAndProceed(currentStepIndex);
    }
  }

  function handleSaveAndProceed(tab: number) {
    const moveOneStep = tab + 1;
    if (!isLastStep) {
      setSubmitAction(undefined);
      next();
      setTab(moveOneStep);
    } else {
      setOpen(false);
    }
  }

  function handleSaveAndSubmit() {
    setOpen(false);
    setSubmitAction(undefined);
  }

  function handleTradeSubmit(formData: { data: MasterTradeParams }) {
    setMasterTrade(formData?.data);
    handleSubmit();
  }

  const tabsConfig = [
    {
      tab: Tab.Booking,
      label: 'Booking',
      component: (
        <EditBookingForm
          trade={masterTrade ?? tradeToEdit}
          onHandleSubmit={handleTradeSubmit}
          closeModal={() => setOpen(!open)}
        />
      ),
    },
    {
      tab: Tab.References,
      label: <div>References</div>,
      component: (
        <EditReferencesForm
          trade={masterTrade ?? tradeToEdit}
          onHandleSubmit={handleTradeSubmit}
          closeModal={() => setOpen(!open)}
        />
      ),
    },
    {
      tab: Tab.Counterparty,
      label: 'Counterparty',
      component: (
        <EditCounterpartiesForm
          trade={masterTrade ?? tradeToEdit}
          onHandleSubmit={handleTradeSubmit}
          closeModal={() => setOpen(!open)}
        />
      ),
    },
    {
      tab: Tab.TermsOfTrade,
      label: 'Terms Of Trade',
      component: (
        <EditTermsOfTradeForm
          trade={masterTrade ?? tradeToEdit}
          onHandleSubmit={handleTradeSubmit}
          closeModal={() => setOpen(!open)}
        />
      ),
    },
  ]

  const {
    step,
    isFirstStep,
    isLastStep,
    currentStepIndex,
    goTo,
    /*back,*/ next,
  } = useMultiStepForm(tabsConfig.map(config => config.component));

  const steps = tabsConfig.map(config => ({
    label: config.label,
    tab: config.tab,
    rightIcon: currentStepIndex === config.tab ? arrowIcon : null,
  }))

  useEffect(() => {
    if (!isFirstStep) {
      goTo(Tab.Booking);
      setTab(Tab.Booking);
    }
  }, [open]);

  return (
    <StepperModal<Tab>
      open={open}
      setOpen={setOpen}
      title="Edit Trade"
      subtitle="Trade Details"
      steps={steps}
      currentTab={tab}
      setTab={setTab}
      goTo={(tabValue: Tab) => {
        setTab(tabValue);
        goTo(tabValue);
      }}
    >
      <>
        {step}
      </>
    </StepperModal>
  );
}
